// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/components/AddDispatchLineDrawer.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  X
} from "lucide-react";

import {
  getDispatchableReservations
} from "../services/dispatchReservationService";

import {
  addDispatchLine
} from "../services/materialDispatchService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function AddDispatchLineDrawer({

  open,

  dispatchId,

  projectId,

  onClose,

  onSuccess

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [selected, setSelected] =
    useState(null);

  const [dispatchQty, setDispatchQty] =
    useState("");

  const [boxNo, setBoxNo] =
    useState("");

  const [weight, setWeight] =
    useState("");


  // ===========================================================================
  // LOAD RESERVATIONS
  // ===========================================================================
  const loadReservations =
    async () => {

      try {

        setLoading(true);

        const response =
          await getDispatchableReservations({

            project_id: projectId
          });

        if (response?.success) {

          setRows(
            response.data || []
          );
        }

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);
      }
    };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    if (open && projectId) {

      loadReservations();
    }

  }, [open, projectId]);


  // ===========================================================================
  // HANDLE SELECT
  // ===========================================================================
  const handleSelect = (
    row
  ) => {

    setSelected(row);

    setDispatchQty(
      row.dispatch_balance_qty
    );

    setBoxNo("");

    setWeight("");
  };


  // ===========================================================================
  // HANDLE SAVE
  // ===========================================================================
  const handleSave = async () => {

    try {

      if (!selected) {

        alert(
          "Please select reservation"
        );

        return;
      }

      if (!dispatchQty) {

        alert(
          "Dispatch qty is required"
        );

        return;
      }

      setSaving(true);

      const response =
        await addDispatchLine({

          dispatch_header_id:
            dispatchId,

          reservation_id:
            selected.reservation_id,

          dispatch_qty:
            dispatchQty,

          box_no:
            boxNo,

          weight:
            weight
        });

      if (response?.success) {

        alert(
          "Dispatch line added successfully"
        );

        if (onSuccess) {

          onSuccess();
        }

        onClose();
      }

    } catch (err) {

      console.error(err);

      alert(

        err?.response?.data?.message ||

        "Failed to add dispatch line"
      );

    } finally {

      setSaving(false);
    }
  };


  // ===========================================================================
  // HIDE
  // ===========================================================================
  if (!open) {

    return null;
  }


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <>

      {/* ================================================================ */}
      {/* OVERLAY */}
      {/* ================================================================ */}
      <div style={styles.overlay} />


      {/* ================================================================ */}
      {/* DRAWER */}
      {/* ================================================================ */}
      <div style={styles.drawer}>


        {/* ============================================================ */}
        {/* HEADER */}
        {/* ============================================================ */}
        <div style={styles.header}>


          <div>

            <div style={styles.title}>
              Add Dispatch Line
            </div>

            <div style={styles.subTitle}>

              Select reservation and
              create dispatch line

            </div>

          </div>


          <button
            style={styles.closeBtn}
            onClick={onClose}
          >

            <X size={18} />

          </button>

        </div>


        {/* ============================================================ */}
        {/* BODY */}
        {/* ============================================================ */}
        <div style={styles.body}>


          {

            loading ? (

              <div style={styles.loader}>
                Loading reservations...
              </div>

            ) : (

              <div style={styles.grid}>


                {

                  rows.map((row) => {

                    const active =
                      selected?.reservation_id ===
                      row.reservation_id;

                    return (

                      <div
                        key={row.reservation_id}
                        style={{
                          ...styles.card,

                          border:
                            active
                              ? "2px solid #2563eb"
                              : "1px solid #e5e7eb"
                        }}
                        onClick={() =>
                          handleSelect(row)
                        }
                      >

                        <div style={styles.material}>

                          {

                            row.material_name
                          }

                        </div>

                        <div style={styles.info}>

                          Code:
                          {" "}

                          {

                            row.material_code
                          }

                        </div>

                        <div style={styles.info}>

                          Balance:
                          {" "}

                          {

                            row.dispatch_balance_qty
                          }

                        </div>

                        <div style={styles.info}>

                          Task:
                          {" "}

                          {

                            row.task_name
                          }

                        </div>

                      </div>
                    );
                  })
                }

              </div>
            )
          }


          {/* ========================================================== */}
          {/* FORM */}
          {/* ========================================================== */}
          {

            selected && (

              <div style={styles.formSection}>


                <div style={styles.formTitle}>
                  Dispatch Information
                </div>


                <div style={styles.formGrid}>


                  <div>

                    <label style={styles.label}>
                      Dispatch Qty
                    </label>

                    <input
                      type="number"
                      value={dispatchQty}
                      onChange={(e) =>
                        setDispatchQty(
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />

                  </div>


                  <div>

                    <label style={styles.label}>
                      Box No
                    </label>

                    <input
                      type="text"
                      value={boxNo}
                      onChange={(e) =>
                        setBoxNo(
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />

                  </div>


                  <div>

                    <label style={styles.label}>
                      Weight
                    </label>

                    <input
                      type="number"
                      value={weight}
                      onChange={(e) =>
                        setWeight(
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />

                  </div>

                </div>

              </div>
            )
          }

        </div>


        {/* ============================================================ */}
        {/* FOOTER */}
        {/* ============================================================ */}
        <div style={styles.footer}>


          <button
            style={styles.cancelBtn}
            onClick={onClose}
          >
            Cancel
          </button>


          <button
            style={styles.submitBtn}
            onClick={handleSave}
            disabled={saving}
          >

            {

              saving
                ? "Saving..."
                : "Add Dispatch Line"
            }

          </button>

        </div>

      </div>

    </>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    zIndex: 1000
  },

  drawer: {

    position: "fixed",

    top: 0,

    right: 0,

    width: "700px",

    height: "100vh",

    background: "#ffffff",

    zIndex: 1001,

    display: "flex",

    flexDirection: "column"
  },

  header: {

    padding: 20,

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  title: {

    fontSize: 20,

    fontWeight: 700
  },

  subTitle: {

    marginTop: 4,

    fontSize: 13,

    color: "#6b7280"
  },

  closeBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border:
      "1px solid #d1d5db",

    background: "#ffffff",

    cursor: "pointer"
  },

  body: {

    flex: 1,

    overflowY: "auto",

    padding: 20
  },

  loader: {

    textAlign: "center",

    padding: 40
  },

  grid: {

    display: "grid",

    gridTemplateColumns:
      "1fr 1fr",

    gap: 16
  },

  card: {

    borderRadius: 12,

    padding: 16,

    cursor: "pointer",

    background: "#ffffff"
  },

  material: {

    fontWeight: 700,

    marginBottom: 8
  },

  info: {

    fontSize: 13,

    color: "#6b7280",

    marginBottom: 4
  },

  formSection: {

    marginTop: 24,

    borderTop:
      "1px solid #e5e7eb",

    paddingTop: 20
  },

  formTitle: {

    fontWeight: 700,

    marginBottom: 16
  },

  formGrid: {

    display: "grid",

    gridTemplateColumns:
      "1fr 1fr 1fr",

    gap: 16
  },

  label: {

    display: "block",

    marginBottom: 8,

    fontSize: 13,

    fontWeight: 600
  },

  input: {

    width: "100%",

    height: 42,

    border:
      "1px solid #d1d5db",

    borderRadius: 8,

    padding: "0 12px"
  },

  footer: {

    padding: 20,

    borderTop:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "flex-end",

    gap: 12
  },

  cancelBtn: {

    border:
      "1px solid #d1d5db",

    background: "#ffffff",

    borderRadius: 8,

    padding: "10px 18px",

    cursor: "pointer"
  },

  submitBtn: {

    background: "#2563eb",

    color: "#ffffff",

    border: "none",

    borderRadius: 8,

    padding: "10px 18px",

    cursor: "pointer"
  }
};